/*
 * Utility functions for handling language codes.
 * Provides fault-tolerant normalization that maps many common variants
 * to the canonical codes required by the backend enum: 'en', 'de', 'fr', 'es', 'it', 'pt', 'nl', 'pl', 'ru', 'ja', 'zh'.
 */

export type SupportedLanguage =
  | 'en' | 'de' | 'fr' | 'es' | 'it' | 'pt' | 'nl' | 'pl' | 'ru' | 'ja' | 'zh';

// Mapping table of lowercase variants to canonical codes
const LANGUAGE_MAP: Record<string, SupportedLanguage> = {
  // English variants
  en: 'en',
  eng: 'en',
  english: 'en',
  'en-us': 'en',
  'en-gb': 'en',
  // German
  de: 'de',
  deu: 'de',
  ger: 'de',
  german: 'de',
  deutsch: 'de',
  // French
  fr: 'fr',
  fra: 'fr',
  fre: 'fr',
  french: 'fr',
  francais: 'fr',
  français: 'fr',
  // Spanish
  es: 'es',
  esp: 'es',
  spa: 'es',
  spanish: 'es',
  espanol: 'es',
  español: 'es',
  // Italian
  it: 'it',
  ita: 'it',
  italian: 'it',
  italiano: 'it',
  // Portuguese
  pt: 'pt',
  por: 'pt',
  portuguese: 'pt',
  portugues: 'pt',
  português: 'pt',
  // Portuguese (Brazil) - map to standard Portuguese
  'pt-br': 'pt',
  'pt_br': 'pt',
  ptbr: 'pt',
  'portuguese-brazil': 'pt',
  'portuguese brazil': 'pt',
  'português brasileiro': 'pt',
  'portugues brasileiro': 'pt',
  brazilian: 'pt',
  brasil: 'pt',
  brazil: 'pt',
  // Dutch
  nl: 'nl',
  nld: 'nl',
  dut: 'nl',
  dutch: 'nl',
  nederlands: 'nl',
  // Polish
  pl: 'pl',
  pol: 'pl',
  polish: 'pl',
  polski: 'pl',
  // Russian
  ru: 'ru',
  rus: 'ru',
  russian: 'ru',
  русский: 'ru',
  // Japanese
  ja: 'ja',
  jpn: 'ja',
  japanese: 'ja',
  日本語: 'ja',
  // Chinese
  zh: 'zh',
  zho: 'zh',
  chi: 'zh',
  chinese: 'zh',
  中文: 'zh',
  'zh-cn': 'zh',
  'zh-tw': 'zh',
  mandarin: 'zh'
};

/**
 * Normalize arbitrary language strings to the canonical, lower-case codes
 * expected by the backend.
 *
 * If the input cannot be mapped, the function falls back to 'en'.
 */
export function normalizeLanguageCode(lang?: string | null): SupportedLanguage {
  if (!lang) {
    return 'en';
  }
  const key = lang.toLowerCase().trim();
  return LANGUAGE_MAP[key] || 'en';
}